import React, { useState, useEffect, useRef } from "react";
import { FC, HTMLAttributes } from "react";
import { CircleFadingPlus, MessageSquareText, X, Mic, ChevronDown, ChevronUp } from "lucide-react";

import {
  useConnectAgent,
  useSelectInputDeviceAgent,
  useEnsureVoiceAgent,
  useInputModeToggleAgent,
} from "rapida-react";

// Define types for devices and ref
type MediaDevice = {
  deviceId: string;
  groupId: string;
  kind: string;
  label: string;
};

interface AudioMessagingActionProps extends HTMLAttributes<HTMLDivElement> {
  placeholder?: string;
}

export const AudioMessagingAction: FC<AudioMessagingActionProps> = ({
  className,
  placeholder,
}) => {
  const ctx = useEnsureVoiceAgent();
  const { handleDisconnectAgent, handleConnectAgent, isConnected } =
    useConnectAgent();

  const { handleTextToggle, handleVoiceToggle } = useInputModeToggleAgent();
  const { devices, activeDeviceId, setActiveMediaDevice } =
    useSelectInputDeviceAgent({
      requestPermissions: true,
    });

  // State for dropdown
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Filter for only audio input devices (microphones)
  const audioInputDevices = devices.filter(
    (device: MediaDevice) => device.kind === "audioinput"
  );

  // Get active device name
  const getActiveDeviceName = (): string => {
    const activeDevice = audioInputDevices.find(
      (device: MediaDevice) => device.deviceId === activeDeviceId
    );
    return activeDevice?.label || "Default Microphone";
  };

  // Orange color palette for dark mode
  const colors = {
    primary: "#f97316", // Orange-500
    primaryLight: "rgba(249, 115, 22, 0.2)", // Orange with opacity
    primaryLighter: "rgba(249, 115, 22, 0.1)", 
    primaryDarker: "rgba(249, 115, 22, 0.3)",
    background: "#1e1e1e", // Dark background
    surfaceLight: "#2d2d2d", // Slightly lighter surface
    surfaceDark: "#1a1a1a", // Slightly darker surface
    textPrimary: "#f3f4f6", // Light text
    textSecondary: "#9ca3af", // Gray text
    border: "rgba(75, 85, 99, 0.3)", // Border color
    danger: "#ef4444", // Red
    dangerLight: "rgba(239, 68, 68, 0.2)" // Red with opacity
  };

  // Define inline styles with dark mode and orange theme
  const styles = {
    container: {
      position: "relative" as const,
      display: "flex" as const,
      alignItems: "center" as const,
      padding: "0.5rem 0.75rem",
      gap: "1rem",
      backgroundColor: colors.background,
      color: colors.textPrimary
    },
    centralContent: {
      display: "flex" as const,
      alignItems: "center" as const,
      justifyContent: "center" as const,
      width: "100%"
    },
    talkButton: {
      display: "flex" as const,
      alignItems: "center" as const, 
      gap: "0.375rem",
      border: `0.5px solid ${colors.primaryLighter}`,
      backgroundColor: colors.surfaceLight,
      borderRadius: "9999px",
      padding: "0.25rem",
      paddingLeft: "1rem",
      paddingRight: "1rem",
      paddingTop: "0.5rem",
      paddingBottom: "0.5rem",
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)",
      color: colors.primary,
      cursor: "pointer"
    },
    buttonText: {
      fontWeight: 500,
      fontSize: "0.875rem"
    },
    buttonContainer: {
      display: "flex" as const,
      alignItems: "center" as const,
      gap: "0.375rem", 
      border: `0.5px solid ${colors.primaryLighter}`,
      backgroundColor: colors.surfaceLight,
      borderRadius: "9999px",
      padding: "0.25rem",
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)"
    },
    textModeButton: {
      backgroundColor: colors.primaryLight,
      backdropFilter: "blur(8px)",
      borderRadius: "9999px", 
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)",
      padding: "0.5rem",
      border: `0.2px solid ${colors.primaryLight}`,
      color: colors.primary,
      cursor: "pointer"
    },
    textModeButtonHover: {
      backgroundColor: colors.primary,
      color: colors.textPrimary
    },
    disconnectButton: {
      backgroundColor: colors.dangerLight,
      backdropFilter: "blur(8px)",
      borderRadius: "9999px", 
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)",
      padding: "0.5rem",
      border: `0.2px solid ${colors.dangerLight}`,
      color: colors.danger,
      cursor: "pointer"
    },
    disconnectButtonHover: {
      backgroundColor: colors.danger,
      color: colors.textPrimary
    },
    iconStyle: {
      width: "1rem",
      height: "1rem"
    },
    // Updated styles for microphone selector with dark theme
    microphoneSelector: {
      position: "relative" as const,
      marginRight: "0.5rem"
    },
    microphoneButton: {
      display: "flex" as const,
      alignItems: "center" as const,
      gap: "0.375rem",
      backgroundColor: colors.primaryLight,
      borderRadius: "9999px",
      padding: "0.5rem",
      border: `0.2px solid ${colors.primaryLight}`,
      color: colors.primary,
      cursor: "pointer"
    },
    microphoneButtonHover: {
      backgroundColor: colors.primaryDarker
    },
    microphoneButtonActive: {
      backgroundColor: colors.primaryDarker
    },
    dropdownContainer: {
      position: "absolute" as const,
      bottom: "calc(100% + 0.5rem)", // Position above instead of below
      right: 0,
      width: "16rem",
      backgroundColor: colors.surfaceLight,
      borderRadius: "0.375rem",
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)",
      border: `1px solid ${colors.border}`,
      zIndex: 50
    },
    dropdownHeader: {
      padding: "0.75rem 1rem",
      borderBottom: `1px solid ${colors.border}`,
      fontWeight: 600,
      fontSize: "0.875rem",
      color: colors.textPrimary
    },
    deviceList: {
      maxHeight: "12rem",
      overflowY: "auto" as const
    },
    deviceItem: {
      padding: "0.75rem 1rem",
      fontSize: "0.875rem",
      cursor: "pointer",
      display: "flex" as const,
      alignItems: "center" as const,
      justifyContent: "space-between" as const,
      color: colors.textSecondary
    },
    deviceItemHover: {
      backgroundColor: colors.surfaceDark
    },
    deviceItemActive: {
      backgroundColor: colors.primaryLighter,
      color: colors.primary
    },
    activeIndicator: {
      width: "0.5rem",
      height: "0.5rem",
      borderRadius: "9999px",
      backgroundColor: colors.primary
    }
  };

  // For hover states
  const [isTextButtonHovered, setIsTextButtonHovered] = useState<boolean>(false);
  const [isDisconnectButtonHovered, setIsDisconnectButtonHovered] = useState<boolean>(false);
  const [isMicButtonHovered, setIsMicButtonHovered] = useState<boolean>(false);
  const [hoveredDeviceId, setHoveredDeviceId] = useState<string | null>(null);

  return (
    <div style={styles.container} className={className}>
      <div style={styles.centralContent}>
        {!isConnected ? (
          <button
            onClick={async () => {
              await handleConnectAgent(ctx);
            }}
            style={styles.talkButton}
          >
            <CircleFadingPlus style={styles.iconStyle} />
            <span style={styles.buttonText}>Click to talk</span>
          </button>
        ) : (
          <div style={styles.buttonContainer}>
            {/* Microphone selector */}
            <div style={styles.microphoneSelector} ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                style={{
                  ...styles.microphoneButton,
                  ...(isMicButtonHovered ? styles.microphoneButtonHover : {}),
                  ...(isDropdownOpen ? styles.microphoneButtonActive : {})
                }}
                onMouseEnter={() => setIsMicButtonHovered(true)}
                onMouseLeave={() => setIsMicButtonHovered(false)}
                title="Select microphone"
              >
                <Mic style={styles.iconStyle} strokeWidth={2} />
                {isDropdownOpen ? (
                  <ChevronUp style={styles.iconStyle} strokeWidth={2} />
                ) : (
                  <ChevronDown style={styles.iconStyle} strokeWidth={2} />
                )}
              </button>

              {/* Dropdown for microphone selection - now positioned ABOVE */}
              {isDropdownOpen && (
                <div style={styles.dropdownContainer}>
                  <div style={styles.dropdownHeader}>Select Microphone</div>
                  <div style={styles.deviceList}>
                    {audioInputDevices.length === 0 ? (
                      <div style={styles.deviceItem}>No microphones found</div>
                    ) : (
                      audioInputDevices.map((device: MediaDevice) => (
                        <div
                          key={device.deviceId}
                          onClick={() => {
                            setActiveMediaDevice(device.deviceId);
                            setIsDropdownOpen(false);
                          }}
                          style={{
                            ...styles.deviceItem,
                            ...(hoveredDeviceId === device.deviceId
                              ? styles.deviceItemHover
                              : {}),
                            ...(activeDeviceId === device.deviceId
                              ? styles.deviceItemActive
                              : {})
                          }}
                          onMouseEnter={() => setHoveredDeviceId(device.deviceId)}
                          onMouseLeave={() => setHoveredDeviceId(null)}
                        >
                          <div style={{ maxWidth: "90%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {device.label || `Microphone ${device.deviceId.substring(0, 5)}`}
                          </div>
                          {activeDeviceId === device.deviceId && (
                            <div style={styles.activeIndicator}></div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Text mode button */}
            <button
              onClick={async () => {
                await handleTextToggle(ctx);
              }}
              style={{
                ...styles.textModeButton,
                ...(isTextButtonHovered ? styles.textModeButtonHover : {})
              }}
              onMouseEnter={() => setIsTextButtonHovered(true)}
              onMouseLeave={() => setIsTextButtonHovered(false)}
            >
              <MessageSquareText 
                style={styles.iconStyle} 
                strokeWidth={2} 
              />
            </button>

            {/* Disconnect button */}
            <button
              onClick={async () => {
                await handleDisconnectAgent(ctx);
                await handleTextToggle(ctx);
              }}
              style={{
                ...styles.disconnectButton,
                ...(isDisconnectButtonHovered ? styles.disconnectButtonHover : {})
              }}
              onMouseEnter={() => setIsDisconnectButtonHovered(true)}
              onMouseLeave={() => setIsDisconnectButtonHovered(false)}
            >
              <X 
                style={styles.iconStyle}
                strokeWidth={2} 
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};